/**
 * DÍA 1 — Spontaneous English.
 *
 * Mecanismos: Interact → Feedback → Repeat → Transfer.
 *
 * Regla de feedback (CURRICULUM.md §4): no corregir durante el primer intento;
 * después, máximo tres correcciones de alto impacto; repetir SOLO una parte; y
 * pasar inmediatamente a una situación distinta.
 */

import type { Sesion } from '../../tipos.js';

export const D1: Sesion = {
  clave: 'd1',
  tipo: 'dia',
  etiqueta: 'Día 1 · Spontaneous English',
  titulo: 'Día 1 · Hablar sin guion',
  corto: 'Hablar',
  objetivo:
    'Producir inglés espontáneo y reutilizar las correcciones en una situación diferente.',
  porQue:
    'Tu dificultad principal no se resuelve acumulando más scripts. Necesitás recuperar y adaptar lenguaje cuando no sabés qué viene después.',
  areasAEvaluar: ['habla_espontanea', 'naturalidad', 'gramatica_fluidez'],
  capturaCorrecciones: true,
  planes: {
    20: {
      duracion: 20,
      pasos: [
        {
          clave: 'd1-conversacion',
          titulo: 'Conversación espontánea',
          minutosSugeridos: 15,
          herramienta: 'chatgpt',
          prompt: 'd1',
          objetivo: 'Hablar sin preparar respuestas.',
          porQue:
            'La imprevisibilidad te obliga a construir lenguaje en tiempo real, que es exactamente lo que falla en una conversación de verdad.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Hablá sin pedir correcciones durante el primer intento.',
            'Aceptá los cambios de tema y las preguntas de seguimiento.',
            'Si te falta una palabra, rodeala en inglés antes de pedir ayuda.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y sostené la conversación. Las correcciones llegan al final, no durante.',
          },
          areas: ['habla_espontanea', 'naturalidad', 'gramatica_fluidez', 'interaccion_turnos'],
        },
        {
          clave: 'd1-registro',
          titulo: 'Registro útil',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 5 minutos.',
          objetivo: 'Conservar solo el material que merezca reaparecer.',
          porQue:
            'Un banco pequeño y activo funciona mejor que una lista interminable. Lo que guardás hoy es lo que el Día 4 te va a hacer recuperar.',
          instrucciones: [
            'Guardá entre 1 y 3 errores o expresiones útiles de la conversación.',
            'Elegí el área de cada uno si la reconocés; si no, dejalo sin clasificar.',
            'No registres cada detalle: solo lo que querés que vuelva a aparecer.',
          ],
        },
      ],
    },

    35: {
      duracion: 35,
      pasos: [
        {
          clave: 'd1-retrieval-previo',
          titulo: 'Retrieval rápido',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          objetivo: 'Activar material previo antes de conversar.',
          elementosBanco: { minimo: 3, maximo: 3 },
          porQue:
            'Recuperar sin mirar fortalece el acceso rápido. Releer solo entrena reconocimiento, que es un músculo distinto.',
          instrucciones: [
            'Abrí el Learning Bank y elegí 3 elementos activos.',
            'Intentá decirlos partiendo de la situación o del significado, sin leer la respuesta.',
            'Si el banco todavía tiene menos de 3 elementos, usá los que haya; si está vacío, saltá este paso y empezá la conversación.',
          ],
          areas: ['recuperacion_lexica'],
        },
        {
          clave: 'd1-conversacion',
          titulo: 'Conversación, feedback y transferencia',
          minutosSugeridos: 25,
          herramienta: 'chatgpt',
          prompt: 'd1',
          objetivo: 'Hablar, corregir poco y volver a usarlo enseguida.',
          porQue:
            'El circuito A → feedback → A mejorada → B nueva evita que la mejora quede amarrada a una sola historia. Si solo repetís A, aprendés esa respuesta, no el recurso.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Hacé el primer intento sin interrupciones docentes.',
            'Pedí solo las tres correcciones principales.',
            'Rehacé SOLO la parte problemática, no toda la conversación.',
            'Transferilas inmediatamente al contexto nuevo que te propongan.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice, conversá sin correcciones, pedí las tres al final, rehacé una parte y pasá a la situación nueva.',
          },
          areas: ['habla_espontanea', 'naturalidad', 'gramatica_fluidez', 'interaccion_turnos'],
        },
        {
          clave: 'd1-registro',
          titulo: 'Registro',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 5 minutos.',
          objetivo: 'Capturar lo que todavía no te sale automático.',
          porQue: 'Eso alimenta los ejercicios de recuperación del Día 4.',
          instrucciones: [
            'Guardá como máximo 3 elementos.',
            'Elegí el área de cada uno si la reconocés; si no, dejalo sin clasificar.',
          ],
        },
      ],
    },

    50: {
      duracion: 50,
      pasos: [
        {
          clave: 'd1-retrieval-previo',
          titulo: 'Retrieval rápido',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          objetivo: 'Activar lenguaje previo antes de empezar a hablar.',
          elementosBanco: { minimo: 3, maximo: 5 },
          porQue: 'Reduce el hueco entre reconocer una expresión y poder producirla a tiempo.',
          instrucciones: [
            'Recuperá entre 3 y 5 elementos del Learning Bank sin mirar la respuesta.',
            'Si el banco todavía está vacío o casi, usá lo que haya y seguí.',
          ],
          areas: ['recuperacion_lexica'],
        },
        {
          clave: 'd1-conversacion-profunda',
          titulo: 'Conversación profunda',
          minutosSugeridos: 35,
          herramienta: 'chatgpt',
          prompt: 'd1',
          objetivo: 'Sostener improvisación bajo presión comunicativa.',
          porQue:
            'Más tiempo permite interrupciones, desacuerdo, reparación y cambios de tema. Una conversación corta no llega a ponerte en aprietos.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Mantené English-only durante la performance.',
            'No busques perfección mientras hablás: buscá seguir hablando.',
            'Pedí las tres correcciones al final y rehacé solo una parte.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y sostené una conversación larga con cambios de tema y desacuerdo. Correcciones al final.',
          },
          areas: ['habla_espontanea', 'naturalidad', 'gramatica_fluidez', 'interaccion_turnos'],
        },
        {
          clave: 'd1-transferencia',
          titulo: 'Transferencia final',
          minutosSugeridos: 5,
          herramienta: 'chatgpt',
          prompt: 'd1',
          objetivo: 'Usar la misma función en un tema completamente diferente.',
          porQue:
            'La transferencia es la prueba de que no memorizaste una respuesta. Si el recurso sobrevive al cambio de tema, es tuyo.',
          instrucciones: [
            'Pedí un contexto totalmente nuevo, sin relación con lo anterior.',
            'Usá al menos una de las correcciones que acabás de recibir.',
          ],
          uso: {
            queHacer:
              'En la misma conversación de ChatGPT, pedí un contexto nuevo y usá ahí una de las correcciones recientes.',
          },
          areas: ['habla_espontanea', 'naturalidad'],
        },
        {
          clave: 'd1-registro',
          titulo: 'Registro',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 5 minutos.',
          objetivo: 'Conservar lo de mayor impacto.',
          porQue: 'Solo lo que debe volver a aparecer en el Día 4.',
          instrucciones: [
            'Guardá como máximo 3 elementos.',
            'Elegí el área de cada uno si la reconocés; si no, dejalo sin clasificar.',
          ],
        },
      ],
    },
  },
};
